import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../../contexts/AuthContext'
import * as api from '../../lib/api'
import { supabase } from '../../lib/supabase'
import { showBrowserNotification } from '../../lib/notifications'
import { useToast } from '../../contexts/ToastContext'
import Modal from '../../components/Modal'
import { QRCodeSVG } from 'qrcode.react'
import './VacanciesPage.css'

// Форматирование числа с пробелами: 35000 → "35 000"
function formatNumber(val) {
  if (!val && val !== 0) return ''
  return Number(val).toLocaleString('ru-RU')
}

// Парсинг числа из строки с пробелами: "35 000" → 35000
function parseFormattedNumber(str) {
  if (!str) return ''
  return str.replace(/\s/g, '').replace(/[^0-9]/g, '')
}

// Статусы воронки
const RESPONSE_STATUSES = {
  new: { label: 'Новый', color: '#EF4444' },
  reviewing: { label: 'Рассмотрение', color: '#F59E0B' },
  interview: { label: 'Собеседование', color: '#3B82F6' },
  accepted: { label: 'Принят', color: '#14B8A6' },
  rejected: { label: 'Отклонён', color: '#6B7280' },
}

const VACANCY_STATUSES = {
  open: { label: 'Открыта', color: '#14B8A6' },
  paused: { label: 'Приостановлена', color: '#F59E0B' },
  closed: { label: 'Закрыта', color: '#6B7280' },
}

const DEFAULT_FORM_FIELDS = [
  { id: 'full_name', label: 'Имя', type: 'text', required: true, enabled: true, system: true },
  { id: 'phone', label: 'Телефон', type: 'phone', required: true, enabled: true, system: true },
  { id: 'age', label: 'Возраст', type: 'number', required: false, enabled: true, system: true },
  { id: 'is_studying', label: 'Учитесь ли вы?', type: 'select', options: ['Нет', 'Да, очно', 'Да, заочно'], required: false, enabled: true, system: true },
  { id: 'duration', label: 'На какой срок планируете?', type: 'select', options: ['Длительный', '3–6 месяцев', 'На лето', 'Не определился'], required: false, enabled: true, system: true },
  { id: 'experience', label: 'Опыт работы', type: 'textarea', required: false, enabled: true, system: true },
  { id: 'comment', label: 'Комментарий', type: 'textarea', required: false, enabled: true, system: true },
]

export default function VacanciesPage() {
  const { profile, activePvz, userPvzs, selectedPvzIds } = useAuth()
  const toast = useToast()

  // Только владелец и админы имеют доступ к вакансиям
  if (profile?.role !== 'owner' && profile?.role !== 'admin' && profile?.role !== 'admin_employee') {
    return <div className="vacancies-page"><div className="vacancies-empty"><p>Доступ только для владельца</p></div></div>
  }
  const [vacancies, setVacancies] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingVacancy, setEditingVacancy] = useState(null)
  const [selectedVacancy, setSelectedVacancy] = useState(null)
  const [responses, setResponses] = useState([])
  const [responsesLoading, setResponsesLoading] = useState(false)
  const [responseFilter, setResponseFilter] = useState('all')
  const [showQRModal, setShowQRModal] = useState(null)
  const [flyerPreviewUrl, setFlyerPreviewUrl] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [copiedLink, setCopiedLink] = useState(false)
  const [showFullFlyerPreview, setShowFullFlyerPreview] = useState(false)
  const [aiGenerating, setAiGenerating] = useState(false)
  const [activeTab, setActiveTab] = useState('vacancies')
  const [allResponses, setAllResponses] = useState([])
  const [allResponsesLoading, setAllResponsesLoading] = useState(false)
  const [hireModal, setHireModal] = useState(null) // { resp, inviteUrl }
  const [formFields, setFormFields] = useState([...DEFAULT_FORM_FIELDS])
  const [showAddField, setShowAddField] = useState(false)
  const [newField, setNewField] = useState({ label: '', type: 'text', required: false, options: '' })


  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    salary_from: '',
    salary_to: '',
    schedule: '',
    requirements: '',
    benefits: '',
    address: '',
    pvz_id: '',
  })

  const orgId = profile?.org_id

  const loadVacancies = useCallback(async () => {
    if (!orgId) return
    try {
      setLoading(true)
      const data = await api.getVacancies(orgId)
      setVacancies(data)
    } catch (err) {
      console.error('Ошибка загрузки вакансий:', err)
    } finally {
      setLoading(false)
    }
  }, [orgId, selectedPvzIds])

  useEffect(() => {
    loadVacancies()
  }, [loadVacancies, selectedPvzIds])

  useEffect(() => {
    const handleUpdate = () => {
      loadVacancies()
      if (activeTab === 'responses') {
        loadAllResponses()
      }
      if (selectedVacancy) {
        openResponses(selectedVacancy)
      }
    }
    window.dispatchEvent(new CustomEvent('vacancies-responses-updated-handled'))
    window.addEventListener('vacancies-responses-updated', handleUpdate)
    return () => window.removeEventListener('vacancies-responses-updated', handleUpdate)
  }, [loadVacancies, activeTab, selectedVacancy])

  useEffect(() => {
    if (!showQRModal) {
      setFlyerPreviewUrl(null)
      setPreviewLoading(false)
      return
    }

    setPreviewLoading(true)
    const timer = setTimeout(() => {
      const svg = document.querySelector('#hidden-qr-code-area svg')
      if (!svg) {
        setPreviewLoading(false)
        return
      }

      try {
        const svgData = new XMLSerializer().serializeToString(svg)
        const qrImg = new Image()

        qrImg.onload = () => {
          const canvas = document.createElement('canvas')
          canvas.width = 800
          canvas.height = 1200
          const ctx = canvas.getContext('2d')
          
          drawFlyerCanvas(ctx, canvas.width, canvas.height, showQRModal, qrImg)
          
          setFlyerPreviewUrl(canvas.toDataURL('image/png'))
          setPreviewLoading(false)
        }
        
        qrImg.onerror = () => {
          console.error('Failed to load QR image for preview')
          setPreviewLoading(false)
        }

        qrImg.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
      } catch (err) {
        console.error('Error generating preview:', err)
        setPreviewLoading(false)
      }
    }, 150)

    return () => clearTimeout(timer)
  }, [showQRModal])

  // Realtime: подписка на новые отклики
  useEffect(() => {
    if (!supabase || !orgId || vacancies.length === 0) return

    const vacancyIds = vacancies.map(v => v.id)

    const channel = supabase
      .channel('vacancy-responses-realtime')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'vacancy_responses',
      }, (payload) => {
        const newResp = payload.new
        if (!vacancyIds.includes(newResp.vacancy_id)) return

        const vacancy = vacancies.find(v => v.id === newResp.vacancy_id)
        const title = vacancy?.title || 'Вакансия'

        // Push-уведомление
        showBrowserNotification(`Новый отклик: ${title}`, {
          body: `${newResp.full_name} — ${newResp.phone}`,
          tag: 'vacancy-response',
          navType: 'vacancy',
        })

        // In-app toast
        toast.info(`Новый отклик: ${newResp.full_name} — ${title}`)

        // Звук
        try { new Audio('/sounds/notification.wav').play() } catch(e) {}

        // Обновить счётчики
        loadVacancies()

        // Добавить в allResponses если вкладка откликов открыта
        setAllResponses(prev => [{ ...newResp, vacancyTitle: title }, ...prev])
      })
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [orgId, vacancies.length])

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      salary_from: '',
      salary_to: '',
      schedule: '',
      requirements: '',
      benefits: '',
      address: activePvz?.address || '',
      pvz_id: activePvz?.id || '',
    })
    setFormFields([...DEFAULT_FORM_FIELDS])
    setShowAddField(false)
    setNewField({ label: '', type: 'text', required: false, options: '' })
  }

  const openCreate = () => {
    resetForm()
    setEditingVacancy(null)
    setShowCreateModal(true)
  }

  const openEdit = (vacancy) => {
    setForm({
      title: vacancy.title || '',
      description: vacancy.description || '',
      salary_from: vacancy.salary_from || '',
      salary_to: vacancy.salary_to || '',
      schedule: vacancy.schedule || '',
      requirements: vacancy.requirements || '',
      benefits: vacancy.benefits || '',
      address: vacancy.address || '',
      pvz_id: vacancy.pvz_id || '',
    })
    setFormFields(vacancy.form_fields || [...DEFAULT_FORM_FIELDS])
    setEditingVacancy(vacancy)
    setShowCreateModal(true)
  }

  // --- AI Settings (Gemini key) ---
  const [aiSettings, setAiSettings] = useState(null)
  useEffect(() => {
    api.getAiSettings().then(s => setAiSettings(s)).catch(() => {})
  }, [])

  // --- AI Generation ---
  const generateWithAI = async () => {
    if (!form.title.trim()) {
      alert('Сначала введите название должности')
      return
    }
    setAiGenerating(true)
    try {
      const prompt = `Ты — HR-ассистент для ПВЗ (пунктов выдачи заказов) Wildberries/Ozon в России.
Составь описание вакансии для должности: "${form.title}".
${form.schedule ? `График: ${form.schedule}` : ''}
${form.salary_from || form.salary_to ? `Зарплата: ${form.salary_from || '?'} — ${form.salary_to || '?'} ₽` : ''}
${form.address ? `Адрес: ${form.address}` : ''}

Ответь ТОЛЬКО в формате JSON (без markdown, без блоков кода, без пояснений):
{
  "description": "краткое описание работы, 2-3 предложения",
  "requirements": "требования к кандидату, каждый пункт с новой строки через •",
  "benefits": "что предлагаем, каждый пункт с новой строки через •",
  "schedule": "рекомендуемый график если не указан"
}

Пиши простым, понятным языком. Без канцеляризмов. Как будто объясняешь другу.`

      if (!aiSettings?.api_key) {
        alert('Ключ Gemini API не настроен. Укажите его в настройках ИИ ассистента.')
        setAiGenerating(false)
        return
      }

      const response = await fetch('/api/gemini', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          model: 'gemini-flash-lite-latest',
          action: 'generateContent',
          apiKey: aiSettings?.api_key || '',
          payload: {
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            generationConfig: { temperature: 0.7, maxOutputTokens: 1024 },
          },
        }),
      })

      if (!response.ok) {
        const errText = await response.text()
        console.error('Gemini API error:', response.status, errText)
        throw new Error(`Ошибка API (${response.status})`)
      }
      const data = await response.json()
      const text = data?.candidates?.[0]?.content?.parts?.[0]?.text
      if (!text) throw new Error('Пустой ответ от ИИ')

      // Парсим JSON из ответа (чистим от markdown если есть)
      const cleaned = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim()
      const parsed = JSON.parse(cleaned)

      setForm(prev => ({
        ...prev,
        description: parsed.description || prev.description,
        requirements: parsed.requirements || prev.requirements,
        benefits: parsed.benefits || prev.benefits,
        schedule: prev.schedule || parsed.schedule || prev.schedule,
      }))
    } catch (err) {
      console.error('AI generation error:', err)
      alert('Не удалось сгенерировать. Попробуйте ещё раз.')
    } finally {
      setAiGenerating(false)
    }
  }

  const handleSave = async () => {
    if (!form.title.trim()) return
    try {
      if (editingVacancy) {
        await api.updateVacancy(editingVacancy.id, {
          title: form.title,
          description: form.description || null,
          salary_from: form.salary_from ? parseInt(form.salary_from) : null,
          salary_to: form.salary_to ? parseInt(form.salary_to) : null,
          schedule: form.schedule || null,
          requirements: form.requirements || null,
          benefits: form.benefits || null,
          address: form.address || null,
          pvz_id: form.pvz_id || null,
          form_fields: formFields,
        })
      } else {
        const pvz = userPvzs.find(p => p.id === form.pvz_id)
        await api.createVacancy(orgId, {
          ...form,
          salary_from: form.salary_from ? parseInt(form.salary_from) : null,
          salary_to: form.salary_to ? parseInt(form.salary_to) : null,
          pvzName: pvz?.name || '',
          created_by: profile.id,
          form_fields: formFields,
        })
      }
      setShowCreateModal(false)
      loadVacancies()
    } catch (err) {
      console.error('Ошибка сохранения вакансии:', err)
      alert('Ошибка: ' + err.message)
    }
  }

  const handleStatusChange = async (vacancyId, newStatus) => {
    try {
      await api.updateVacancy(vacancyId, { status: newStatus })
      loadVacancies()
    } catch (err) {
      console.error('Ошибка смены статуса:', err)
    }
  }

  const handleDelete = async (vacancyId) => {
    if (!confirm('Удалить вакансию и все отклики? Это действие нельзя отменить.')) return
    try {
      await api.deleteVacancy(vacancyId)
      loadVacancies()
    } catch (err) {
      console.error('Ошибка удаления:', err)
    }
  }

  // --- Responses ---
  // Авто-отметка new → reviewing при просмотре
  const markAsReviewing = async (responses) => {
    const newOnes = responses.filter(r => r.status === 'new')
    for (const r of newOnes) {
      try {
        await api.updateVacancyResponse(r.id, { status: 'reviewing' })
      } catch (e) { /* silent */ }
    }
    if (newOnes.length > 0) {
      loadVacancies() // обновить счётчики
    }
    return responses.map(r => r.status === 'new' ? { ...r, status: 'reviewing' } : r)
  }

  const openResponses = async (vacancy) => {
    setSelectedVacancy(vacancy)
    setResponseFilter('all')
    setResponsesLoading(true)
    try {
      const data = await api.getVacancyResponses(vacancy.id)
      const updated = await markAsReviewing(data)
      setResponses(updated)
    } catch (err) {
      console.error('Ошибка загрузки откликов:', err)
    } finally {
      setResponsesLoading(false)
    }
  }

  const handleResponseStatus = async (responseId, newStatus) => {
    try {
      await api.updateVacancyResponse(responseId, { status: newStatus })
      setResponses(prev => prev.map(r => r.id === responseId ? { ...r, status: newStatus } : r))
      setAllResponses(prev => prev.map(r => r.id === responseId ? { ...r, status: newStatus } : r))
    } catch (err) {
      console.error('Ошибка обновления отклика:', err)
    }
  }

  const handleResponseNotes = async (responseId, notes) => {
    try {
      await api.updateVacancyResponse(responseId, { notes })
      setResponses(prev => prev.map(r => r.id === responseId ? { ...r, notes } : r))
      setAllResponses(prev => prev.map(r => r.id === responseId ? { ...r, notes } : r))
    } catch (err) {
      console.error('Ошибка сохранения заметки:', err)
    }
  }

  const handleResponseRating = async (responseId, rating) => {
    try {
      await api.updateVacancyResponse(responseId, { rating })
      setResponses(prev => prev.map(r => r.id === responseId ? { ...r, rating } : r))
      setAllResponses(prev => prev.map(r => r.id === responseId ? { ...r, rating } : r))
    } catch (err) {
      console.error('Ошибка сохранения рейтинга:', err)
    }
  }

  // Компонент звёзд
  const StarRating = ({ value, onChange }) => {
    const [hover, setHover] = useState(0)
    const display = hover || value || 0
    return (
      <div className="star-rating" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            className={`star-btn ${star <= display ? 'active' : ''}`}
            onClick={() => onChange(star === value ? null : star)}
            onMouseEnter={() => setHover(star)}
            title={star === value ? 'Убрать оценку' : `Оценка: ${star}`}
          >
            ★
          </button>
        ))}
      </div>
    )
  }

  const handleDeleteResponse = async (responseId) => {
    if (!confirm('Удалить отклик?')) return
    try {
      await api.deleteVacancyResponse(responseId)
      setResponses(prev => prev.filter(r => r.id !== responseId))
      setAllResponses(prev => prev.filter(r => r.id !== responseId))
      // Обновляем счётчики в вакансиях
      loadVacancies()
    } catch (err) {
      console.error('Ошибка удаления отклика:', err)
    }
  }

  // Принять на работу — создать инвайт + показать ссылку
  const handleAcceptCandidate = async (resp) => {
    try {
      // 1. Статус → accepted
      await api.updateVacancyResponse(resp.id, { status: 'accepted' })
      setResponses(prev => prev.map(r => r.id === resp.id ? { ...r, status: 'accepted' } : r))
      setAllResponses(prev => prev.map(r => r.id === resp.id ? { ...r, status: 'accepted' } : r))

      // 2. Найти pvz_id вакансии
      const vacancy = selectedVacancy || vacancies.find(v => v.id === resp.vacancy_id)
      const pvzId = vacancy?.pvz_id || activePvz?.id
      
      if (!pvzId) {
        setHireModal({ resp, inviteUrl: null, error: 'Не удалось определить ПВЗ. Создайте инвайт вручную в Настройках → Команда.' })
        return
      }

      // 3. Создать инвайт
      const invite = await api.createInvite(pvzId, orgId)
      const inviteUrl = `https://pvzraschet.ru?invite=${invite.code}`
      
      setHireModal({ resp, inviteUrl, code: invite.code })
    } catch (err) {
      console.error('Ошибка принятия кандидата:', err)
    }
  }

  // Всегда прод-домен для публичных ссылок
  const PROD_URL = 'https://pvzraschet.ru'
  const getVacancyUrl = (slug) => `${PROD_URL}/vacancy/${slug}`

  const copyLink = (slug) => {
    navigator.clipboard.writeText(getVacancyUrl(slug)).then(() => {
      setCopiedLink(true)
      setTimeout(() => setCopiedLink(false), 2000)
    })
  }

  const downloadBeautifulQR = (vacancy) => {
    const svg = document.querySelector('#hidden-qr-code-area svg')
    if (!svg) return
    
    const svgData = new XMLSerializer().serializeToString(svg)
    const qrImg = new Image()
    
    qrImg.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = 800
      canvas.height = 1200
      const ctx = canvas.getContext('2d')
      
      drawFlyerCanvas(ctx, canvas.width, canvas.height, vacancy, qrImg)
      
      const link = document.createElement('a')
      const cleanTitle = vacancy.title ? vacancy.title.replace(/[^a-zA-Zа-яА-Я0-9]/g, '_') : 'vacancy'
      link.download = `Flyer_${vacancy.platform === 'ozon' ? 'Ozon' : 'WB'}_${cleanTitle}.png`
      link.href = canvas.toDataURL('image/png')
      link.click()
    }
    
    qrImg.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
  }

  const downloadFlyerPDF = async (vacancy) => {
    const svg = document.querySelector('#hidden-qr-code-area svg')
    if (!svg) return
    
    const jspdfModule = await import('jspdf/dist/jspdf.umd.min.js')
    const jsPDF = jspdfModule.jsPDF || jspdfModule.default || jspdfModule
    const svgData = new XMLSerializer().serializeToString(svg)
    const qrImg = new Image()
    
    qrImg.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = 800
      canvas.height = 1200
      const ctx = canvas.getContext('2d')
      
      drawFlyerCanvas(ctx, canvas.width, canvas.height, vacancy, qrImg)
      
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })
      
      const imgData = canvas.toDataURL('image/png')
      doc.addImage(imgData, 'PNG', 0, 0, 210, 297)
      
      const cleanTitle = vacancy.title ? vacancy.title.replace(/[^a-zA-Zа-яА-Я0-9]/g, '_') : 'vacancy'
      doc.save(`Flyer_${vacancy.platform === 'ozon' ? 'Ozon' : 'WB'}_${cleanTitle}.pdf`)
    }
    
    qrImg.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
  }

  const filteredResponses = responseFilter === 'all' 
    ? responses 
    : responses.filter(r => r.status === responseFilter)

  const formatSalary = (from, to) => {
    if (!from && !to) return null
    if (from && to) return `${from.toLocaleString('ru')} — ${to.toLocaleString('ru')} ₽`
    if (from) return `от ${from.toLocaleString('ru')} ₽`
    return `до ${to.toLocaleString('ru')} ₽`
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Загрузка всех откликов при переключении на вкладку
  const loadAllResponses = async () => {
    setAllResponsesLoading(true)
    try {
      const all = []
      for (const v of vacancies) {
        const resps = await api.getVacancyResponses(v.id)
        resps.forEach(r => all.push({ ...r, vacancyTitle: v.title }))
      }
      all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      const updated = await markAsReviewing(all)
      setAllResponses(updated)
    } catch (err) {
      console.error('Ошибка загрузки откликов:', err)
    } finally {
      setAllResponsesLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'responses' && vacancies.length > 0 && allResponses.length === 0) {
      loadAllResponses()
    }
  }, [activeTab, vacancies])



  const totalNewResponses = vacancies.reduce((sum, v) => sum + (v.new_responses_count || 0), 0)
  
  const filteredAllResponses = responseFilter === 'all'
    ? allResponses
    : allResponses.filter(r => r.status === responseFilter)

  // --- Render: Responses View (per vacancy) ---
  if (selectedVacancy) {
    return (
      <div className="vacancies-page">
        <div className="vacancies-header">
          <button className="btn btn-ghost" onClick={() => setSelectedVacancy(null)}>
            ← Назад
          </button>
          <h2>Отклики: {selectedVacancy.title}</h2>
        </div>

        <div className="response-filters">
          {['all', 'new', 'reviewing', 'interview', 'accepted', 'rejected'].map(f => {
            const count = f === 'all' ? responses.length : responses.filter(r => r.status === f).length
            const label = f === 'all' ? 'Все' : RESPONSE_STATUSES[f]?.label
            return (
              <button
                key={f}
                className={`response-filter-btn ${responseFilter === f ? 'active' : ''}`}
                onClick={() => setResponseFilter(f)}
              >
                {label} ({count})
              </button>
            )
          })}
        </div>

        {responsesLoading ? (
          <div className="vacancies-empty">Загрузка откликов...</div>
        ) : filteredResponses.length === 0 ? (
          <div className="vacancies-empty">
            <div className="vacancies-empty-icon">Пусто</div>
            <p>Нет откликов{responseFilter !== 'all' ? ' с таким статусом' : ''}</p>
          </div>
        ) : (
          <div className="responses-list">
            {filteredResponses.map(resp => (
              <div key={resp.id} className={`response-card response-status-${resp.status}`}>
                <div className="response-card-header">
                  <span className="response-status-badge" style={{ background: RESPONSE_STATUSES[resp.status]?.color }}>
                    {RESPONSE_STATUSES[resp.status]?.label}
                  </span>
                  <span className="response-source">
                    {resp.source === 'qr' ? 'QR-код' : resp.source === 'website' ? 'Сайт' : resp.source}
                  </span>
                </div>

                  <div className="response-card-body">
                    <div className="response-info">
                      <h4>{resp.full_name}</h4>
                      <a href={`tel:${resp.phone}`} className="response-phone">{resp.phone}</a>
                      {resp.answers ? (
                        <div className="response-answers">
                          {Object.entries(resp.answers)
                            .filter(([k]) => k !== 'full_name' && k !== 'phone')
                            .map(([key, val]) => {
                              const vacancy = selectedVacancy || vacancies.find(v => v.id === resp.vacancy_id)
                              const fieldDef = (vacancy?.form_fields || DEFAULT_FORM_FIELDS).find(f => f.id === key)
                              const label = fieldDef?.label || key
                              return (
                                <p key={key} className="response-answer-item">
                                  <span className="response-answer-label">{label}:</span> {val}
                                </p>
                              )
                            })}
                        </div>
                      ) : (
                        <>
                          {resp.experience && <p className="response-exp">{resp.experience}</p>}
                          {resp.comment && <p className="response-comment">«{resp.comment}»</p>}
                        </>
                      )}
                      <span className="response-date">{formatDate(resp.created_at)}</span>
                    </div>

                    <StarRating value={resp.rating} onChange={(r) => handleResponseRating(resp.id, r)} />

                    <div className="response-notes-area">
                      <textarea
                        className="response-notes-input"
                        placeholder="Заметка..."
                        value={resp.notes || ''}
                        onChange={(e) => setResponses(prev => prev.map(r => r.id === resp.id ? { ...r, notes: e.target.value } : r))}
                        onBlur={(e) => handleResponseNotes(resp.id, e.target.value)}
                      />
                    </div>
                  </div>

                <div className="response-card-actions">
                  <a href={`tel:${resp.phone}`} className="btn btn-sm btn-ghost">Позвонить</a>
                  {resp.status === 'new' && (
                    <>
                      <button className="btn btn-sm btn-primary" onClick={() => handleResponseStatus(resp.id, 'interview')}>
                        Собеседование
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleResponseStatus(resp.id, 'rejected')}>
                        Отклонить
                      </button>
                    </>
                  )}
                  {resp.status === 'reviewing' && (
                    <>
                      <button className="btn btn-sm btn-primary" onClick={() => handleResponseStatus(resp.id, 'interview')}>
                        Собеседование
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleResponseStatus(resp.id, 'rejected')}>
                        Отклонить
                      </button>
                    </>
                  )}
                  {resp.status === 'interview' && (
                    <>
                      <button className="btn btn-sm btn-success" onClick={() => handleAcceptCandidate(resp)}>
                        Принять на работу
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleResponseStatus(resp.id, 'rejected')}>
                        Отклонить
                      </button>
                    </>
                  )}
                  <button className="btn btn-sm btn-ghost text-danger" onClick={() => handleDeleteResponse(resp.id)} title="Удалить отклик">
                    Удалить
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // --- Render: Main ---
  return (
    <div className="vacancies-page">
      <div className="vacancies-header">
        <div className="vacancies-tabs">
          <button
            className={`vacancies-tab ${activeTab === 'vacancies' ? 'active' : ''}`}
            onClick={() => setActiveTab('vacancies')}
          >
            Вакансии {vacancies.length > 0 && <span className="tab-count">{vacancies.length}</span>}
          </button>
          <button
            className={`vacancies-tab ${activeTab === 'responses' ? 'active' : ''}`}
            onClick={() => { setActiveTab('responses'); setResponseFilter('all') }}
          >
            Отклики {totalNewResponses > 0 && <span className="tab-count tab-count-new">{totalNewResponses}</span>}
          </button>

        </div>
        {activeTab === 'vacancies' && (
          <button className="btn btn-primary" onClick={openCreate}>
            + Создать вакансию
          </button>
        )}
      </div>

      {activeTab === 'responses' ? (
        /* ====== TAB: ОТКЛИКИ ====== */
        <>
          <div className="response-filters">
            {['all', 'new', 'reviewing', 'interview', 'accepted', 'rejected'].map(f => {
              const count = f === 'all' ? allResponses.length : allResponses.filter(r => r.status === f).length
              const label = f === 'all' ? 'Все' : RESPONSE_STATUSES[f]?.label
              return (
                <button
                  key={f}
                  className={`response-filter-btn ${responseFilter === f ? 'active' : ''}`}
                  onClick={() => setResponseFilter(f)}
                >
                  {label} ({count})
                </button>
              )
            })}
          </div>

          {allResponsesLoading ? (
            <div className="vacancies-empty">Загрузка откликов...</div>
          ) : filteredAllResponses.length === 0 ? (
            <div className="vacancies-empty">
              <h3>Нет откликов</h3>
              <p>Когда кандидаты откликнутся на ваши вакансии, они появятся здесь</p>
            </div>
          ) : (
            <div className="responses-list">
              {filteredAllResponses.map(resp => (
                <div key={resp.id} className={`response-card response-status-${resp.status}`}>
                  <div className="response-card-header">
                    <span className="response-vacancy-tag">{resp.vacancyTitle}</span>
                    <span className="response-status-badge" style={{ background: RESPONSE_STATUSES[resp.status]?.color }}>
                      {RESPONSE_STATUSES[resp.status]?.label}
                    </span>
                  </div>
                  <div className="response-card-body">
                    <div className="response-info">
                      <h4>{resp.full_name}</h4>
                      <a href={`tel:${resp.phone}`} className="response-phone">{resp.phone}</a>
                      {resp.answers ? (
                        <div className="response-answers">
                          {Object.entries(resp.answers)
                            .filter(([k]) => k !== 'full_name' && k !== 'phone')
                            .map(([key, val]) => {
                              const vacancy = vacancies.find(v => v.id === resp.vacancy_id)
                              const fieldDef = (vacancy?.form_fields || DEFAULT_FORM_FIELDS).find(f => f.id === key)
                              const label = fieldDef?.label || key
                              return (
                                <p key={key} className="response-answer-item">
                                  <span className="response-answer-label">{label}:</span> {val}
                                </p>
                              )
                            })}
                        </div>
                      ) : (
                        <>
                          {resp.experience && <p className="response-exp">{resp.experience}</p>}
                          {resp.comment && <p className="response-comment">«{resp.comment}»</p>}
                        </>
                      )}
                      <span className="response-date">{formatDate(resp.created_at)}</span>
                    </div>

                    <StarRating value={resp.rating} onChange={(r) => handleResponseRating(resp.id, r)} />

                    <div className="response-notes-area">
                      <textarea
                        className="response-notes-input"
                        placeholder="Заметка..."
                        value={resp.notes || ''}
                        onChange={(e) => setAllResponses(prev => prev.map(r => r.id === resp.id ? { ...r, notes: e.target.value } : r))}
                        onBlur={(e) => handleResponseNotes(resp.id, e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="response-card-actions">
                    <a href={`tel:${resp.phone}`} className="btn btn-sm btn-ghost">Позвонить</a>
                    {(resp.status === 'new' || resp.status === 'reviewing') && (
                      <button className="btn btn-sm btn-primary" onClick={() => handleResponseStatus(resp.id, 'interview')}>
                        Собеседование
                      </button>
                    )}
                    {resp.status === 'interview' && (
                      <button className="btn btn-sm btn-success" onClick={() => handleAcceptCandidate(resp)}>
                        Принять на работу
                      </button>
                    )}
                    {resp.status !== 'rejected' && resp.status !== 'accepted' && (
                      <button className="btn btn-sm btn-danger" onClick={() => handleResponseStatus(resp.id, 'rejected')}>
                        Отклонить
                      </button>
                    )}
                    <button className="btn btn-sm btn-ghost text-danger" onClick={() => handleDeleteResponse(resp.id)}>
                      Удалить
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        /* ====== TAB: ВАКАНСИИ ====== */
        <>

      {loading ? (
        <div className="vacancies-list">
          {[1, 2, 3].map(i => (
            <div key={i} className="card vacancy-card" style={{ opacity: 0.7, padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div className="vacancy-card-top" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div className="vacancy-card-title-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div className="skeleton" style={{ width: '8px', height: '8px', borderRadius: '50%' }} />
                  <div className="skeleton" style={{ height: '20px', width: `${40 + i * 10}%` }} />
                </div>
                <div className="vacancy-card-meta" style={{ marginTop: '4px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <div className="skeleton" style={{ height: '14px', width: '80px' }} />
                  <div className="skeleton" style={{ height: '14px', width: '100px' }} />
                  <div className="skeleton" style={{ height: '14px', width: '150px' }} />
                </div>
              </div>
              <div className="vacancy-card-stats" style={{ display: 'flex', gap: '8px', borderTop: '1px solid var(--surface-border)', paddingTop: '12px', marginTop: '4px' }}>
                <div className="skeleton" style={{ height: '14px', width: '120px' }} />
              </div>
              <div className="vacancy-card-actions" style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <div className="skeleton" style={{ height: '32px', width: '80px', borderRadius: '8px' }} />
                <div className="skeleton" style={{ height: '32px', width: '60px', borderRadius: '8px' }} />
                <div className="skeleton" style={{ height: '32px', width: '100px', borderRadius: '8px' }} />
              </div>
            </div>
          ))}
        </div>
      ) : vacancies.length === 0 ? (
        <div className="vacancies-empty">
          <div className="vacancies-empty-icon">Пусто</div>
          <h3>Нет вакансий</h3>
          <p>Создайте первую вакансию — повесьте QR-код на дверь ПВЗ и получайте отклики!</p>
          <button className="btn btn-primary" onClick={openCreate} style={{ marginTop: '16px' }}>
            + Создать вакансию
          </button>
        </div>
      ) : (
        <div className="vacancies-list">
          {vacancies.map(v => (
            <div key={v.id} className={`vacancy-card vacancy-status-${v.status}`}>
              <div className="vacancy-card-top">
                <div className="vacancy-card-title-row">
                  <span className="vacancy-status-dot" style={{ background: VACANCY_STATUSES[v.status]?.color }}/>
                  <h3>{v.title}</h3>
                  {v.status !== 'open' && (
                    <span className="vacancy-status-label" style={{ color: VACANCY_STATUSES[v.status]?.color }}>
                      ({VACANCY_STATUSES[v.status]?.label})
                    </span>
                  )}
                </div>
                <div className="vacancy-card-meta">
                  {formatSalary(v.salary_from, v.salary_to) && (
                    <span className="vacancy-meta-item">{formatSalary(v.salary_from, v.salary_to)}</span>
                  )}
                  {v.schedule && <span className="vacancy-meta-item">{v.schedule}</span>}
                  {(v.address || v.pvz?.address) && (
                    <span className="vacancy-meta-item">{v.address || v.pvz?.address}</span>
                  )}
                </div>
              </div>

              <div className="vacancy-card-stats">
                <span>{v.views_count || 0} просмотров</span>
                <span className="vacancy-stats-separator">|</span>
                <span 
                  className={`vacancy-responses-link ${v.new_responses_count > 0 ? 'has-new' : ''}`}
                  onClick={() => openResponses(v)}
                >
                  {v.responses_count} {v.responses_count === 1 ? 'отклик' : 'откликов'}
                  {v.new_responses_count > 0 && (
                    <span className="vacancy-new-badge">{v.new_responses_count} новый</span>
                  )}
                </span>
              </div>

              <div className="vacancy-card-actions">
                <button className="btn btn-sm btn-ghost" onClick={() => copyLink(v.public_slug)} title="Копировать ссылку">
                  {copiedLink ? 'Скопировано' : 'Ссылка'}
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => setShowQRModal(v)} title="QR-код">
                  QR-код
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => openEdit(v)} title="Редактировать">
                  Ред.
                </button>
                {v.status === 'open' ? (
                  <button className="btn btn-sm btn-ghost" onClick={() => handleStatusChange(v.id, 'paused')}>
                    Пауза
                  </button>
                ) : v.status === 'paused' ? (
                  <button className="btn btn-sm btn-ghost" onClick={() => handleStatusChange(v.id, 'open')}>
                    Открыть
                  </button>
                ) : null}
                <button className="btn btn-sm btn-ghost text-danger" onClick={() => handleDelete(v.id)} title="Удалить">
                  Удалить
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {/* Create/Edit Modal */}
      <Modal 
        isOpen={showCreateModal}
        title={editingVacancy ? 'Редактировать вакансию' : 'Новая вакансия'} 
        onClose={() => setShowCreateModal(false)}
      >
        <div className="vacancy-form">
          <div className="input-group">
            <label>Должность *</label>
            <input
              className="input"
              type="text"
              placeholder="Сотрудник ПВЗ Wildberries"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
            />
          </div>

          <button
            type="button"
            className="btn btn-ai-generate"
            onClick={generateWithAI}
            disabled={aiGenerating || !form.title.trim()}
          >
            {aiGenerating ? (
              <><span className="ai-spinner"/>Генерирую...</>
            ) : (
              <>Заполнить с ИИ</>
            )}
          </button>

          <div className="input-group">
            <label>Описание</label>
            <textarea
              className="input vacancy-textarea"
              placeholder="Расскажите подробнее о работе..."
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={3}
            />
          </div>

          <div className="vacancy-form-row">
            <div className="input-group">
              <label>Зарплата от, ₽</label>
              <input
                className="input"
                type="text"
                inputMode="numeric"
                placeholder="35 000"
                value={form.salary_from ? formatNumber(form.salary_from) : ''}
                onChange={e => setForm({ ...form, salary_from: parseFormattedNumber(e.target.value) })}
              />
            </div>
            <div className="input-group">
              <label>Зарплата до, ₽</label>
              <input
                className="input"
                type="text"
                inputMode="numeric"
                placeholder="50 000"
                value={form.salary_to ? formatNumber(form.salary_to) : ''}
                onChange={e => setForm({ ...form, salary_to: parseFormattedNumber(e.target.value) })}
              />
            </div>
          </div>

          <div className="input-group">
            <label>График</label>
            <input
              className="input"
              type="text"
              placeholder="2/2, 10:00—22:00"
              value={form.schedule}
              onChange={e => setForm({ ...form, schedule: e.target.value })}
            />
          </div>

          <div className="input-group">
            <label>Адрес</label>
            <input
              className="input"
              type="text"
              placeholder="г. Каменск-Уральский, ул. Пивоварова, 51"
              value={form.address}
              onChange={e => setForm({ ...form, address: e.target.value })}
            />
          </div>

          <div className="input-group">
            <label>Требования</label>
            <textarea
              className="input vacancy-textarea"
              placeholder="Пунктуальность, ответственность..."
              value={form.requirements}
              onChange={e => setForm({ ...form, requirements: e.target.value })}
              rows={2}
            />
          </div>

          <div className="input-group">
            <label>Что предлагаем</label>
            <textarea
              className="input vacancy-textarea"
              placeholder="Оформление по ТК, стабильная ЗП + бонусы..."
              value={form.benefits}
              onChange={e => setForm({ ...form, benefits: e.target.value })}
              rows={2}
            />
          </div>

          {userPvzs.length > 1 && (
            <div className="input-group">
              <label>Привязать к ПВЗ</label>
              <select
                className="input"
                value={form.pvz_id}
                onChange={e => {
                  const pvz = userPvzs.find(p => p.id === e.target.value)
                  setForm({ ...form, pvz_id: e.target.value, address: pvz?.address || form.address })
                }}
              >
                <option value="">Не привязывать</option>
                {userPvzs.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Редактор полей анкеты */}
          <div className="form-fields-editor">
            <div className="form-fields-title"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle',marginRight:'6px'}}><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>Поля анкеты кандидата</div>
            <p className="form-fields-hint">Настройте вопросы, которые увидит кандидат при отклике</p>
            
            <div className="form-fields-list">
              {formFields.map((field, idx) => (
                <div key={field.id} className={`form-field-item ${!field.enabled ? 'disabled' : ''}`}>
                  <div className="form-field-toggle">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={field.enabled}
                        disabled={field.id === 'full_name' || field.id === 'phone'}
                        onChange={() => setFormFields(prev => prev.map((f, i) => i === idx ? { ...f, enabled: !f.enabled } : f))}
                      />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                  <div className="form-field-info">
                    <span className="form-field-label">
                      {field.label}
                      {field.required && <span className="form-field-required">*</span>}
                    </span>
                    <span className="form-field-type">
                      {field.type === 'text' ? 'Текст' : field.type === 'phone' ? 'Телефон' : field.type === 'number' ? 'Число' : field.type === 'textarea' ? 'Многострочный' : field.type === 'select' ? 'Список' : field.type}
                    </span>
                  </div>
                  <div className="form-field-actions-mini">
                    {!field.system && (
                      <>
                        <button
                          className="form-field-req-toggle"
                          title={field.required ? 'Сделать необязательным' : 'Сделать обязательным'}
                          onClick={() => setFormFields(prev => prev.map((f, i) => i === idx ? { ...f, required: !f.required } : f))}
                        >
                          {field.required ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                        </button>
                        <button
                          className="form-field-delete"
                          title="Удалить"
                          onClick={() => setFormFields(prev => prev.filter((_, i) => i !== idx))}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </>
                    )}
                    {field.system && field.id !== 'full_name' && field.id !== 'phone' && (
                      <button
                        className="form-field-req-toggle"
                        title={field.required ? 'Сделать необязательным' : 'Сделать обязательным'}
                        onClick={() => setFormFields(prev => prev.map((f, i) => i === idx ? { ...f, required: !f.required } : f))}
                      >
                        {field.required ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {showAddField ? (
              <div className="add-field-form">
                <div className="input-group">
                  <label>Текст вопроса</label>
                  <input
                    className="input"
                    type="text"
                    placeholder="Например: Есть ли мед. книжка?"
                    value={newField.label}
                    onChange={e => setNewField({ ...newField, label: e.target.value })}
                  />
                </div>
                <div className="add-field-row">
                  <div className="input-group">
                    <label>Тип ответа</label>
                    <select className="input" value={newField.type} onChange={e => setNewField({ ...newField, type: e.target.value })}>
                      <option value="text">Текст</option>
                      <option value="number">Число</option>
                      <option value="textarea">Многострочный</option>
                      <option value="select">Список вариантов</option>
                    </select>
                  </div>
                  <div className="input-group">
                    <label>&nbsp;</label>
                    <label className="checkbox-label">
                      <input type="checkbox" checked={newField.required} onChange={e => setNewField({ ...newField, required: e.target.checked })} />
                      Обязательный
                    </label>
                  </div>
                </div>
                {newField.type === 'select' && (
                  <div className="input-group">
                    <label>Варианты (через запятую)</label>
                    <input
                      className="input"
                      type="text"
                      placeholder="Да, Нет"
                      value={newField.options}
                      onChange={e => setNewField({ ...newField, options: e.target.value })}
                    />
                  </div>
                )}
                <div className="add-field-actions">
                  <button className="btn btn-ghost btn-sm" onClick={() => { setShowAddField(false); setNewField({ label: '', type: 'text', required: false, options: '' }) }}>
                    Отмена
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!newField.label.trim()}
                    onClick={() => {
                      const id = 'custom_' + Date.now()
                      const field = {
                        id,
                        label: newField.label.trim(),
                        type: newField.type,
                        required: newField.required,
                        enabled: true,
                        system: false,
                      }
                      if (newField.type === 'select' && newField.options.trim()) {
                        field.options = newField.options.split(',').map(o => o.trim()).filter(Boolean)
                      }
                      setFormFields(prev => [...prev, field])
                      setShowAddField(false)
                      setNewField({ label: '', type: 'text', required: false, options: '' })
                    }}
                  >
                    Добавить
                  </button>
                </div>
              </div>
            ) : (
              <button className="btn btn-ghost btn-sm add-field-btn" onClick={() => setShowAddField(true)}>
                + Добавить свой вопрос
              </button>
            )}
          </div>

          <div className="vacancy-form-actions">
            <button className="btn btn-ghost" onClick={() => setShowCreateModal(false)}>
              Отмена
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={!form.title.trim()}>
              {editingVacancy ? 'Сохранить' : 'Опубликовать'}
            </button>
          </div>
        </div>
      </Modal>

      {/* QR Modal */}
      <Modal isOpen={!!showQRModal} title="Флаер вакансии с QR-кодом" onClose={() => setShowQRModal(null)}>
        {showQRModal && (
          <div className="qr-modal-content" style={{ textAlign: 'center', padding: '10px 0' }}>
            <h3 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '6px' }}>{showQRModal.title}</h3>
            {showQRModal.pvz_name && <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>{showQRModal.pvz_name}</p>}
            
            {/* Hidden QR SVG for generation */}
            <div id="hidden-qr-code-area" style={{ display: 'none' }}>
              <QRCodeSVG
                value={getVacancyUrl(showQRModal.public_slug)}
                size={250}
                level="H"
                bgColor="#ffffff"
                fgColor="#000000"
                includeMargin={false}
              />
            </div>

            {/* Flyer Preview */}
            <div className="flyer-preview-container" style={{ minHeight: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-hover)', borderRadius: '16px', padding: '16px', marginBottom: '16px', border: '1px solid var(--border-color)', position: 'relative' }}>
              {previewLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                  <div className="app-loading-spinner"></div>
                  <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Генерация превью флаера...</span>
                </div>
              ) : flyerPreviewUrl ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                  <img 
                    src={flyerPreviewUrl} 
                    alt="Предпросмотр флаера" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '180px', 
                      borderRadius: '8px', 
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)', 
                      display: 'block',
                      border: '1px solid rgba(0,0,0,0.08)'
                    }} 
                  />
                  <button 
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowFullFlyerPreview(true)}
                    style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      borderRadius: '8px'
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                    </svg>
                    Предпросмотр
                  </button>
                </div>
              ) : (
                <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Не удалось создать предпросмотр</span>
              )}
            </div>

            <p className="qr-link-text" style={{ fontSize: '13px', color: 'var(--text-secondary)', wordBreak: 'break-all', marginBottom: '8px' }}>
              Ссылка для отклика: <a href={getVacancyUrl(showQRModal.public_slug)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--wb-primary)', fontWeight: 600 }}>{getVacancyUrl(showQRModal.public_slug)}</a>
            </p>
            <p className="qr-hint" style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '24px' }}>
              Распечатайте этот флаер в формате А4 и повесьте на дверь или информационную доску ПВЗ.
            </p>

            <div className="qr-actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <button className="btn btn-ghost" onClick={() => copyLink(showQRModal.public_slug)} style={{ padding: '10px 16px' }}>
                Копировать ссылку
              </button>
              <button className="btn btn-secondary" onClick={() => downloadBeautifulQR(showQRModal)} disabled={previewLoading || !flyerPreviewUrl} style={{ padding: '10px 16px' }}>
                Скачать PNG
              </button>
              <button className="btn btn-primary" onClick={() => downloadFlyerPDF(showQRModal)} disabled={previewLoading || !flyerPreviewUrl} style={{ padding: '10px 16px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Скачать PDF (А4)
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Fullscreen Flyer Preview Overlay */}
      {showFullFlyerPreview && flyerPreviewUrl && createPortal(
        <div 
          className="flyer-full-preview-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            backdropFilter: 'blur(8px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeIn 0.2s ease-out',
            padding: '20px'
          }}
          onClick={() => setShowFullFlyerPreview(false)}
        >
          <div 
            style={{
              position: 'relative',
              maxWidth: '90%',
              maxHeight: '90%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              type="button"
              style={{
                position: 'absolute',
                top: '-40px',
                right: '-10px',
                background: 'rgba(255, 255, 255, 0.15)',
                border: 'none',
                color: 'white',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '18px'
              }}
              onClick={() => setShowFullFlyerPreview(false)}
              title="Закрыть"
            >
              ✕
            </button>
            <img 
              src={flyerPreviewUrl} 
              alt="Крупный предпросмотр флаера" 
              style={{
                maxWidth: '100%',
                maxHeight: '85vh',
                borderRadius: '16px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                border: '1px solid rgba(255,255,255,0.1)'
              }}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Hire Modal */}
      <Modal
        isOpen={!!hireModal}
        title="Кандидат принят"
        onClose={() => setHireModal(null)}
      >
        {hireModal && (
          <div className="hire-modal-content">
            <div className="hire-success-icon">
              <svg viewBox="0 0 52 52" width="56" height="56">
                <circle cx="26" cy="26" r="24" fill="none" stroke="#14B8A6" strokeWidth="2.5"/>
                <path fill="none" stroke="#14B8A6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
              </svg>
            </div>
            <h3 className="hire-name">{hireModal.resp.full_name}</h3>
            <p className="hire-phone">{hireModal.resp.phone}</p>

            {hireModal.error ? (
              <div className="hire-error">{hireModal.error}</div>
            ) : (
              <>
                <div className="hire-invite-section">
                  <p className="hire-desc">Отправьте эту ссылку кандидату для регистрации в системе:</p>
                  <div className="hire-invite-box">
                    <code className="hire-invite-url">{hireModal.inviteUrl}</code>
                    <button className="btn btn-sm btn-primary" onClick={() => {
                      navigator.clipboard.writeText(hireModal.inviteUrl)
                      setCopiedLink(true)
                      setTimeout(() => setCopiedLink(false), 2000)
                    }}>
                      {copiedLink ? 'Скопировано' : 'Копировать'}
                    </button>
                  </div>
                  <p className="hire-code-label">Код приглашения: <strong>{hireModal.code}</strong></p>
                </div>
              </>
            )}

            <div className="hire-actions" style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setHireModal(null)}>Закрыть</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

function drawFlyerCanvas(ctx, w, h, vacancy, qrImg) {
  // Draw background
  const isOzon = vacancy.platform === 'ozon'
  if (isOzon) {
    // Ozon blue background
    ctx.fillStyle = '#005BFF'
    ctx.fillRect(0, 0, w, h)
    
    // Ozon pink decorative circle/accent
    ctx.fillStyle = '#F91155'
    ctx.beginPath()
    ctx.arc(w, 150, 250, 0, Math.PI * 2)
    ctx.fill()
    
    // Secondary decorative circle
    ctx.fillStyle = 'rgba(255, 255, 255, 0.08)'
    ctx.beginPath()
    ctx.arc(0, 950, 300, 0, Math.PI * 2)
    ctx.fill()
  } else {
    // Wildberries gradient background
    const grad = ctx.createLinearGradient(0, 0, w, h)
    grad.addColorStop(0, '#510065') // WB Purple
    grad.addColorStop(0.5, '#86007b') // WB Magenta-purple
    grad.addColorStop(1, '#cb11ab') // WB Pinkish magenta
    ctx.fillStyle = grad
    ctx.fillRect(0, 0, w, h)
    
    // WB decorative circle/accent
    ctx.fillStyle = 'rgba(255, 255, 255, 0.06)'
    ctx.beginPath()
    ctx.arc(w, 1050, 250, 0, Math.PI * 2)
    ctx.fill()
    
    ctx.fillStyle = 'rgba(255, 255, 255, 0.05)'
    ctx.beginPath()
    ctx.arc(0, 150, 350, 0, Math.PI * 2)
    ctx.fill()
  }
  
  // Title/Header text
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  
  // "РАБОТА РЯДОМ С ДОМОМ"
  ctx.fillStyle = '#FFFFFF'
  ctx.font = '900 44px "Montserrat", "Inter", "Segoe UI", sans-serif'
  ctx.fillText('РАБОТА РЯДОМ С ДОМОМ', w / 2, 110)
  
  // Subtext
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
  ctx.font = '600 20px "Montserrat", "Inter", "Segoe UI", sans-serif'
  ctx.fillText('ПРИСОЕДИНЯЙТЕСЬ К НАШЕЙ КОМАНДЕ', w / 2, 165)
  
  // Draw white card in the center
  const cardX = 60
  const cardY = 210
  const cardW = w - 120
  const cardH = h - 320
  const radius = 24
  
  // Draw shadow
  ctx.shadowColor = 'rgba(0, 0, 0, 0.25)'
  ctx.shadowBlur = 40
  ctx.shadowOffsetX = 0
  ctx.shadowOffsetY = 15
  
  // Helper function to draw rounded rect
  const drawRoundRect = (c, x, y, width, height, r) => {
    c.beginPath()
    c.moveTo(x + r, y)
    c.lineTo(x + width - r, y)
    c.quadraticCurveTo(x + width, y, x + width, y + r)
    c.lineTo(x + width, y + height - r)
    c.quadraticCurveTo(x + width, y + height, x + width - r, y + height)
    c.lineTo(x + r, y + height)
    c.quadraticCurveTo(x, y + height, x, y + height - r)
    c.lineTo(x, y + r)
    c.quadraticCurveTo(x, y, x + r, y)
    c.closePath()
  }
  
  ctx.fillStyle = '#FFFFFF'
  drawRoundRect(ctx, cardX, cardY, cardW, cardH, radius)
  ctx.fill()
  
  // Reset shadow
  ctx.shadowBlur = 0
  ctx.shadowOffsetY = 0
  
  // Platform label on card
  ctx.textAlign = 'center'
  if (isOzon) {
    ctx.fillStyle = '#005BFF'
    ctx.font = '900 24px "Montserrat", "Inter", "Segoe UI", sans-serif'
    ctx.fillText('ПАРТНЕРСКИЙ ПУНКТ OZON', w / 2, 260)
  } else {
    ctx.fillStyle = '#cb11ab'
    ctx.font = '900 24px "Montserrat", "Inter", "Segoe UI", sans-serif'
    ctx.fillText('ПУНКТ ВЫДАЧИ WILDBERRIES', w / 2, 260)
  }
  
  // Separator line
  ctx.strokeStyle = '#E5E7EB'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(100, 295)
  ctx.lineTo(w - 100, 295)
  ctx.stroke()
  
  // Vacancy Title
  ctx.fillStyle = '#111827'
  ctx.font = '800 50px "Montserrat", "Inter", "Segoe UI", sans-serif'
  const titleText = (vacancy.title || 'Менеджер ПВЗ').toUpperCase()
  
  // Handle very long title word wrap
  if (titleText.length > 22) {
    ctx.font = '800 38px "Montserrat", "Inter", "Segoe UI", sans-serif'
    const words = titleText.split(' ')
    let line1 = ''
    let line2 = ''
    words.forEach(w => {
      if ((line1 + ' ' + w).length <= 22) {
        line1 = line1 ? line1 + ' ' + w : w
      } else {
        line2 = line2 ? line2 + ' ' + w : w
      }
    })
    ctx.fillText(line1, w / 2, 350)
    if (line2) {
      ctx.fillText(line2, w / 2, 400)
    }
  } else {
    ctx.fillText(titleText, w / 2, 360)
  }
  
  // PVZ Address
  ctx.fillStyle = '#4B5563'
  ctx.font = '600 22px "Montserrat", "Inter", "Segoe UI", sans-serif'
  const addressText = vacancy.address || vacancy.pvz_address || (vacancy.pvz && vacancy.pvz.address) || 'Адрес уточняется'
  
  const addressY = titleText.length > 22 ? 455 : 420
  if (addressText.length > 42) {
    const words = addressText.split(' ')
    let line1 = ''
    let line2 = ''
    words.forEach(w => {
      if ((line1 + ' ' + w).length <= 42) {
        line1 = line1 ? line1 + ' ' + w : w
      } else {
        line2 = line2 ? line2 + ' ' + w : w
      }
    })
    ctx.fillText(line1, w / 2, addressY)
    if (line2) {
      ctx.fillText(line2, w / 2, addressY + 30)
    }
  } else {
    ctx.fillText(addressText, w / 2, addressY)
  }
  
  // Details (Salary and Schedule cards)
  const detailsY = titleText.length > 22 ? 530 : 490
  
  const salaryText = vacancy.salary_from || vacancy.salary_to
    ? `${vacancy.salary_from ? 'от ' + vacancy.salary_from.toLocaleString('ru') : ''}${vacancy.salary_to ? ' до ' + vacancy.salary_to.toLocaleString('ru') : ''} ₽`
    : 'Зарплата договорная'
    
  // Salary Box
  ctx.fillStyle = isOzon ? 'rgba(0, 91, 255, 0.06)' : 'rgba(203, 17, 171, 0.05)'
  drawRoundRect(ctx, 100, detailsY, 285, 95, 14)
  ctx.fill()
  
  // Salary Label
  ctx.fillStyle = '#6B7280'
  ctx.font = '700 13px "Montserrat", "Inter", "Segoe UI", sans-serif'
  ctx.fillText('ЗАРАБОТНАЯ ПЛАТА', 242, detailsY + 28)
  
  // Salary Value
  ctx.fillStyle = isOzon ? '#005BFF' : '#cb11ab'
  ctx.font = '800 25px "Montserrat", "Inter", "Segoe UI", sans-serif'
  ctx.fillText(salaryText, 242, detailsY + 62)
  
  // Schedule Box
  ctx.fillStyle = '#F3F4F6'
  drawRoundRect(ctx, 415, detailsY, 285, 95, 14)
  ctx.fill()
  
  // Schedule Label
  ctx.fillStyle = '#6B7280'
  ctx.font = '700 13px "Montserrat", "Inter", "Segoe UI", sans-serif'
  ctx.fillText('ГРАФИК РАБОТЫ', 557, detailsY + 28)
  
  // Schedule Value
  ctx.fillStyle = '#111827'
  ctx.font = '800 24px "Montserrat", "Inter", "Segoe UI", sans-serif'
  ctx.fillText(vacancy.schedule || 'Сменный график', 557, detailsY + 62)
  
  // QR Code Container background box
  const qrBoxY = detailsY + 125
  const qrSize = 250
  const qrX = w / 2 - (qrSize / 2)
  
  ctx.fillStyle = '#FFFFFF'
  ctx.strokeStyle = '#E5E7EB'
  ctx.lineWidth = 1
  drawRoundRect(ctx, qrX - 20, qrBoxY - 20, qrSize + 40, qrSize + 40, 18)
  ctx.fill()
  ctx.stroke()
  
  // Draw QR image
  ctx.drawImage(qrImg, qrX, qrBoxY, qrSize, qrSize)
  
  // Scan instruction text below QR
  ctx.textAlign = 'center'
  ctx.fillStyle = '#111827'
  ctx.font = '800 24px "Montserrat", "Inter", "Segoe UI", sans-serif'
  ctx.fillText('ОТСКАНИРУЙТЕ ДЛЯ ОТКЛИКА', w / 2, qrBoxY + qrSize + 55)
  
  ctx.fillStyle = '#4B5563'
  ctx.font = '600 15px "Montserrat", "Inter", "Segoe UI", sans-serif'
  ctx.fillText('Наведите камеру телефона на QR-код, чтобы заполнить анкету', w / 2, qrBoxY + qrSize + 85)
  
  // Footer in Russian (removes English technical PVZ System string)
  const footerY = 1140
  ctx.fillStyle = 'rgba(255, 255, 255, 0.45)'
  ctx.font = '700 14px "Montserrat", "Inter", "Segoe UI", sans-serif'
  ctx.fillText('ИНФОРМАЦИОННЫЙ ФЛАЕР ПАРТНЕРА • PVZRASCHET.RU', w / 2, footerY)
}
